'use client';

import { useMemo, useState, useEffect, useCallback, createContext } from 'react';

import { useCookies } from 'src/hooks/use-cookies';
import { useLocalStorage } from 'src/hooks/use-local-storage';

import { setDisplayTimezone } from 'src/utils/format-time';
import { getBrowserTimezone, validateTimezone } from 'src/services/timezone-service';

import { STORAGE_KEY, defaultSettings } from '../config-settings';

export const SettingsContext = createContext(undefined);

export const SettingsConsumer = SettingsContext.Consumer;

export function SettingsProvider({ children, settings, caches = 'localStorage' }) {
  const cookies = useCookies(STORAGE_KEY, settings, defaultSettings);

  const localStorage = useLocalStorage(STORAGE_KEY, settings);

  const values = caches === 'cookie' ? cookies : localStorage;

  const [openDrawer, setOpenDrawer] = useState(false);

  // Keep the module-level display zone (used by every fDate/fTime call site) in
  // step with the selected setting, so switching the selector re-renders all
  // times without any screen doing its own conversion.
  useEffect(() => {
    const tz = values.state?.timezone;
    setDisplayTimezone(validateTimezone(tz) ? tz : getBrowserTimezone());
  }, [values.state?.timezone]);

  const onToggleDrawer = useCallback(() => {
    setOpenDrawer((prev) => !prev);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
  }, []);

  const memoizedValue = useMemo(
    () => ({
      ...values.state,
      canReset: values.canReset,
      onReset: values.resetState,
      onUpdate: values.setState,
      onUpdateField: values.setField,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
    }),
    [
      values.state,
      values.setField,
      values.setState,
      values.canReset,
      values.resetState,
      openDrawer,
      onCloseDrawer,
      onToggleDrawer,
    ]
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
